import { Attachment, getFileThumbnail, PreviewFile, useModal } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import debounce from 'lodash/debounce'
import React, {
  ChangeEvent,
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useQueryClient } from 'react-query'

import { useIsEInvoicingOnly } from '@features/bills/hooks/useIsEInvoicingOnly'
import { Bill } from '@features/bills/types/bill'
import { IntegrationStatus } from '@features/bills/types/integrationStatus'
import { EInvoiceStatus } from '@features/eInvoicing/constants'
import { getEInvoiceLifecycleFromDataJson } from '@features/eInvoicing/utils'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { ExpensesTrackingEvents } from '@views/receipts/enums/tracking'
import {
  getReceiptBill,
  getReceiptOwnerIntegrationStatuses,
} from '@views/receipts/utils/getReceiptOwnerIntegrationStatuses'

import { QueryKeys } from '../../../../../enums/queryKeys'
import { useSegment } from '../../../../../hooks'
import { AttachmentType } from '../../../../attachments/enums/attachmentType'
import { useUpdateAttachment } from '../../../../attachments/hooks/useUpdateAttachment'
import { isAttachmentBlocked } from '../../../utils/isAttachmentBlocked'

interface ContextState {
  amount?: number
  attachment?: Attachment
  bill?: Bill | null
  attachmentId?: string
  close: () => void
  onOpenDuplicate?: (attachmentId: string) => void
  createdDate: string | undefined
  description: string
  documentDate: string | undefined
  fileName: string
  files: PreviewFile[]
  handleDescriptionChange: (event: ChangeEvent<HTMLTextAreaElement>) => void
  handleEInvoiceRejection: () => void
  handleManageUploadButtonClick: () => void
  isEDocument: boolean
  isEInvoicingOnly: boolean
  isFrenchEInvoice: boolean
  footerButton?: ReactElement
  eInvoiceStatus: EInvoiceStatus | null
  reasonCode: string | null
  integrationStatuses?: IntegrationStatus[]
  isBlocked: boolean
  isShoeboxReadOnly: boolean
  saveStatus: SaveAttachmentStatus
  supplier?: string
  isDuplicate: boolean
}

const AttachmentPreviewModalContext = createContext<ContextState | undefined>(undefined)

type SaveAttachmentStatus = 'idle' | 'saving' | 'saved' | 'error'

interface AttachmentPreviewModalContextProps {
  attachment?: Attachment
  children: ReactNode
  footerButton?: ReactElement
  isShoeboxReadOnly?: boolean
  modalId: string
  onFileSelect?: () => void
  onOpenDuplicate?: (attachmentId: string) => void
  trackingSource?: string
}

export const AttachmentPreviewModalContextProvider = ({
  attachment,
  children,
  footerButton,
  isShoeboxReadOnly = false,
  modalId,
  onFileSelect,
  onOpenDuplicate,
  trackingSource,
}: AttachmentPreviewModalContextProps): ReactElement => {
  const { amount, createdTime, documentDate, id, file, supplier, type, comment } = attachment || {}
  const eInvoiceLifecycle =
    type === AttachmentType.EDocument && file?.dataJson ? getEInvoiceLifecycleFromDataJson(file.dataJson) : null
  const eInvoiceStatus = eInvoiceLifecycle?.eInvoiceStatus ?? null
  const reasonCode = eInvoiceLifecycle?.reasonCode ?? null
  const isBlocked = attachment ? isAttachmentBlocked(attachment) : false
  const integrationStatuses = attachment ? getReceiptOwnerIntegrationStatuses(attachment) : undefined
  const bill = attachment ? getReceiptBill(attachment) : undefined
  const [description, setDescription] = useState<string>(comment || '')
  const [saveStatus, setSaveStatus] = useState<SaveAttachmentStatus>('idle')
  const queryClient = useQueryClient()
  const { close } = useModal(modalId)
  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const isEInvoicingOnly = useIsEInvoicingOnly()

  useEffect(() => {
    setDescription(comment || '')
  }, [comment])

  const { update } = useUpdateAttachment({
    onSuccess: async () => {
      await queryClient.invalidateQueries(QueryKeys.AttachmentsList)
      setSaveStatus('saved')
      setTimeout(() => setSaveStatus('idle'), 2000)
    },
    onError: (error) => {
      trackError(error)

      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 5000)
    },
  })

  const isEDocument = type === AttachmentType.EDocument
  // Dispute/suspend and the reworked CTA layout are for the French market only;
  // other markets keep the legacy refuse flow (c.f. ReceiptRejection).
  const isFrenchEInvoice = isEDocument && organization?.countryId === 'FR'
  const [documentDateParsed] = documentDate ? documentDate.split('T') : []
  const [createdDateParsed] = createdTime ? createdTime.split('T') : []

  const files: PreviewFile[] = useMemo(
    () =>
      file
        ? [
            {
              src: file.isPdf ? file.downloadUrl : getFileThumbnail(file, 'medium'),
              srcZoom: file.isPdf ? file.downloadUrl : getFileThumbnail(file, 'large'),
              srcDownload: file.downloadUrl,
            },
          ]
        : [],
    [file],
  )

  const debouncedUpdate = useMemo(
    () =>
      debounce((newComment: string) => {
        if (!id) {
          return
        }
        update({ id, comment: newComment })
      }, 1000),
    [update, id],
  )

  const handleDescriptionChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      if (saveStatus !== 'saving') {
        setSaveStatus('saving')
      }
      const value = event.target.value
      setDescription?.(value)
      debouncedUpdate(value)
    },
    [debouncedUpdate, saveStatus, setDescription],
  )

  const handleManageUploadButtonClick = useCallback(() => {
    track(ExpensesTrackingEvents.AttachmentSelectButtonClick, {
      source: trackingSource,
    })
    onFileSelect?.()
    close()
  }, [onFileSelect, close, track, trackingSource])

  const handleEInvoiceRejection = useCallback(() => {
    close()
  }, [close])

  return (
    <AttachmentPreviewModalContext.Provider
      value={{
        amount,
        attachment,
        attachmentId: id,
        bill,
        close,
        onOpenDuplicate,
        createdDate: createdDateParsed,
        description,
        documentDate: documentDateParsed,
        fileName: file?.fileName || '-',
        files,
        footerButton,
        handleDescriptionChange,
        handleEInvoiceRejection,
        handleManageUploadButtonClick,
        isEDocument,
        isEInvoicingOnly,
        isFrenchEInvoice,
        eInvoiceStatus,
        reasonCode,
        integrationStatuses,
        isBlocked,
        isShoeboxReadOnly,
        saveStatus,
        supplier,
        isDuplicate: !!attachment?.file?.parentDuplicateFileId,
      }}
    >
      {children}
    </AttachmentPreviewModalContext.Provider>
  )
}

export const useAttachmentPreviewModal = () => {
  const context = useContext(AttachmentPreviewModalContext)

  if (!context) {
    throw new Error('AttachmentPreviewModalContextProvider is missing in the module!')
  }

  return context
}
